import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateCurrentUser(
    $primary_markets: [String!]
    $specialized_businesses: [String!]
    $practice_areas: [String!]
    $why_values: [String!]
    $language_ids: [ID!]
    $interest_ids: [ID!]
    $sport_ids: [ID!]
    $designations: [String!]
    $advisor_licenses: [String!]
  ) {
    updateCurrentUser(
      primary_markets: $primary_markets
      specialized_businesses: $specialized_businesses
      practice_areas: $practice_areas
      why_values: $why_values
      language_ids: $language_ids
      interest_ids: $interest_ids
      sport_ids: $sport_ids
      designations: $designations
      advisor_licenses: $advisor_licenses
    )
  }
`;

const MultiSelectEditModal = ({
  title,
  selected,
  options,
  maxSelections,
  onClose,
  onSave,
  field,
  user,
  onError,
  isLanguages,
  languageOptions,
  isInterestsAndSports,
  interestOptions,
  sportOptions,
  allowCustom,
}) => {
  const intl = useIntl();

  // Helper function to format display text: remove underscores and capitalize each word
  const formatDisplayText = (text) => {
    if (!text) return '';

    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Filter selected items to only include valid options from the current list
  const validSelectedItems = (selected || []).filter(item => {
    if (isLanguages && languageOptions) {
      return languageOptions.some(lang => lang.name === item);
    } else if (isInterestsAndSports && (interestOptions || sportOptions)) {
      return (interestOptions || []).some(int => int.name === item) ||
             (sportOptions || []).some(sport => sport.name === item);
    } else {
      // If allowCustom is true, include items not in options (custom values)
      return allowCustom || options.includes(item);
    }
  });

  const [selectedItems, setSelectedItems] = useState(validSelectedItems);
  const [customValue, setCustomValue] = useState('');

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: () => {
      onSave(selectedItems);
    },
    onError: (error) => onError(error),
    refetchQueries: ['GetCurrentUserProfile'],
    awaitRefetchQueries: true,
  });

  const toggleItem = (item) => {
    if (selectedItems.includes(item)) {
      setSelectedItems(selectedItems.filter((i) => i !== item));
    } else if (selectedItems.length < maxSelections) {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const addCustomItem = () => {
    const trimmedValue = customValue.trim();
    if (trimmedValue && !selectedItems.includes(trimmedValue) && selectedItems.length < maxSelections) {
      setSelectedItems([...selectedItems, trimmedValue]);
      setCustomValue('');
    }
  };

  const handleCustomKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCustomItem();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let variables = {};

    if (isLanguages && languageOptions) {
      const languageIds = languageOptions
        .filter((lang) => selectedItems.includes(lang.name))
        .map((lang) => lang.id);
      variables.language_ids = languageIds;
    } else if (isInterestsAndSports && interestOptions && sportOptions) {
      const interestIds = interestOptions
        .filter((int) => selectedItems.includes(int.name))
        .map((int) => int.id);
      const sportIds = sportOptions
        .filter((sport) => selectedItems.includes(sport.name))
        .map((sport) => sport.id);
      variables.interest_ids = interestIds;
      variables.sport_ids = sportIds;
    } else {
      variables[field] = selectedItems;
    }

    updateUser({ variables });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
      <div className="bg-succession-bg-secondary rounded-lg max-w-2xl w-full border border-succession-border flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }} onClick={(e) => e.stopPropagation()}>
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-succession-border flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-succession-text-primary">{title}</h2>
            <p className="text-sm text-succession-text-muted mt-1">
              {intl.formatMessage({ id: 'succession.profile_edit.modals.multi_select_edit.selection_count' }, { max: maxSelections, selected: selectedItems.length })}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors">
            <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
            {/* Show selected custom items if allowCustom */}
            {allowCustom && selectedItems.some(item => !options.includes(item)) && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.multi_select_edit.selected_custom_items' })}</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedItems.filter(item => !options.includes(item)).map((item) => (
                    <div
                      key={item}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-succession-primary text-white rounded-full text-sm"
                    >
                      <span>{formatDisplayText(item)}</span>
                      <button
                        type="button"
                        onClick={() => toggleItem(item)}
                        className="hover:bg-white hover:bg-opacity-20 rounded-full p-0.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available options section */}
            {options.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-succession-text-primary mb-3">{intl.formatMessage({ id: 'succession.profile_edit.modals.multi_select_edit.available_options' })}</h3>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {options.map((option) => {
                const isSelected = selectedItems.includes(option);
                const isDisabled = !isSelected && selectedItems.length >= maxSelections;

                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => toggleItem(option)}
                    disabled={isDisabled}
                    className={`px-4 py-3 rounded-lg border transition-all text-left ${
                      isSelected
                        ? 'border-succession-primary bg-succession-primary text-white'
                        : isDisabled
                        ? 'border-succession-border bg-succession-bg-primary text-succession-text-muted cursor-not-allowed opacity-50'
                        : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>{formatDisplayText(option)}</span>
                      {isSelected && (
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Custom Input Section */}
            {allowCustom && (
              <div className="mt-6 pt-6 border-t border-succession-border">
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.multi_select_edit.add_custom_item' })}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    onKeyPress={handleCustomKeyPress}
                    placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.multi_select_edit.custom_placeholder' })}
                    className="flex-1 px-4 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                    disabled={selectedItems.length >= maxSelections}
                  />
                  <button
                    type="button"
                    onClick={addCustomItem}
                    disabled={!customValue.trim() || selectedItems.length >= maxSelections}
                    className="px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {intl.formatMessage({ id: 'succession.profile_edit.common.add' })}
                  </button>
                </div>
                <p className="mt-1 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.multi_select_edit.custom_hint' })}
                </p>
              </div>
            )}
          </div>

          {/* Fixed Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 pt-4 border-t border-succession-border flex-shrink-0 bg-succession-bg-secondary">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors">
              {intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}
            </button>
            <button type="submit" disabled={loading}
              className={`px-4 py-2 rounded-lg text-white transition-colors ${loading ? 'bg-succession-primary opacity-50 cursor-not-allowed' : 'bg-succession-primary hover:opacity-80'}`}>
              {loading ? intl.formatMessage({ id: 'succession.profile_edit.common.saving' }) : intl.formatMessage({ id: 'succession.profile_edit.common.save_changes' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

MultiSelectEditModal.propTypes = {
  title: PropTypes.string.isRequired,
  selected: PropTypes.array,
  options: PropTypes.array.isRequired,
  maxSelections: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  field: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  onError: PropTypes.func.isRequired,
  isLanguages: PropTypes.bool,
  languageOptions: PropTypes.array,
  isInterestsAndSports: PropTypes.bool,
  interestOptions: PropTypes.array,
  sportOptions: PropTypes.array,
  allowCustom: PropTypes.bool,
};

export default MultiSelectEditModal;
